import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import {
  Search,
  Leaf,
  Archive,
  Layers,
  ListTodo,
  Navigation,
  Zap,
  AlertCircle,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSearch, UnifiedSearchResult } from '../../hooks/useSearch';
import { clsx } from 'clsx';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_ICONS = {
  species: Leaf,
  pest: AlertCircle,
  disease: AlertCircle,
  area: Layers,
  batch: Archive,
  task: ListTodo,
  action: Zap,
  nav: Navigation,
};

const TYPE_COLORS = {
  species: 'text-green-500',
  pest: 'text-red-500',
  disease: 'text-orange-500',
  area: 'text-cyan-500',
  batch: 'text-teal-500',
  task: 'text-amber-500',
  action: 'text-yellow-500',
  nav: 'text-gray-500',
};

export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { results, isLoading, hasQuery } = useSearch(query);

  // Close on Escape or select
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        // Toggle is handled by parent, but we might want to close if open?
        // Usually handled by TopNavLayout
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSelect = (item: UnifiedSearchResult) => {
    if (item.url) {
      navigate(item.url);
      onClose();
      setQuery('');
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-2xl transform divide-y divide-gray-100 dark:divide-white/5 overflow-hidden rounded-2xl bg-white/80 dark:bg-zinc-900/90 backdrop-blur-xl shadow-2xl ring-1 ring-black/5 transition-all">
              <Combobox onChange={handleSelect}>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <Combobox.Input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Hledat rostliny, záhony, úkoly... (Cmd+K)"
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>

                {hasQuery && (
                  <Combobox.Options
                    static
                    className="max-h-[60vh] overflow-y-auto p-2 scroll-py-2 text-sm text-gray-800 dark:text-zinc-200"
                  >
                    {results.length > 0 ? (
                      results.map((item) => {
                        const Icon = TYPE_ICONS[item.type] || Search;
                        return (
                          <Combobox.Option
                            key={`${item.type}-${item.id}`}
                            value={item}
                            className={({ active }) =>
                              clsx(
                                'flex cursor-default select-none items-center rounded-xl px-3 py-3 transition-colors',
                                active &&
                                  'bg-primary-500/10 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400',
                              )
                            }
                          >
                            {({ active }) => (
                              <>
                                <div
                                  className={clsx(
                                    'flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-white dark:bg-white/5 shadow-sm ring-1 ring-black/5',
                                    TYPE_COLORS[item.type],
                                  )}
                                >
                                  <Icon className="h-5 w-5" aria-hidden="true" />
                                </div>
                                <div className="ml-4 flex-auto">
                                  <p
                                    className={clsx(
                                      'font-semibold',
                                      active
                                        ? 'text-primary-700 dark:text-primary-300'
                                        : 'text-gray-900 dark:text-white',
                                    )}
                                  >
                                    {item.title}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-zinc-400">
                                    {item.subtitle}
                                  </p>
                                </div>
                                {active && (
                                  <ChevronRight
                                    className="ml-3 h-5 w-5 flex-none text-primary-500"
                                    aria-hidden="true"
                                  />
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        );
                      })
                    ) : (
                      <div className="px-4 py-14 text-center sm:px-14">
                        <AlertCircle className="mx-auto h-6 w-6 text-gray-400" />
                        <p className="mt-4 text-sm text-gray-900 dark:text-white">
                          Žádné výsledky pro "{query}"
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          Zkuste hledat něco jiného nebo zkontrolujte překlepy.
                        </p>
                      </div>
                    )}
                  </Combobox.Options>
                )}

                {!hasQuery && (
                  <div className="p-4 text-center text-xs text-gray-500 dark:text-zinc-400 border-t border-gray-100 dark:border-white/5">
                    Zadejte alespoň 2 znaky pro vyhledávání...
                  </div>
                )}

                {isLoading && (
                  <div className="absolute right-4 top-3.5">
                    <Clock className="h-5 w-5 animate-spin text-primary-500" />
                  </div>
                )}

                <div className="flex items-center justify-between bg-gray-50 dark:bg-white/5 px-4 py-2.5 text-xs text-gray-500">
                  <div className="flex gap-4">
                    <span>
                      <kbd className="font-sans font-bold">↑↓</kbd> navigace
                    </span>
                    <span>
                      <kbd className="font-sans font-bold">↵</kbd> vybrat
                    </span>
                    <span>
                      <kbd className="font-sans font-bold">esc</kbd> zavřít
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    Powered by <span className="font-bold text-primary-600">Rootify AI</span>
                  </div>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
